import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PasswordInput } from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, KeyRound } from "lucide-react";
import { changePassword } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { currentUser, refreshPermissions } = useAuth();
  const forced = !!currentUser?.must_change_password;

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setStatus("error");
      setMessage("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus("error");
      setMessage("Passwords do not match.");
      return;
    }

    setStatus("loading");
    setMessage("");
    try {
      await changePassword(currentPassword, newPassword);
      await refreshPermissions();
      navigate("/", { replace: true });
    } catch (err: any) {
      setStatus("error");
      setMessage(err?.message || "Failed to change password.");
    }
  };

  // Say so as soon as the two differ, not only after pressing the button
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  return (
    <div className="flex justify-center px-4 sm:px-6 py-8 sm:py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <KeyRound className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold leading-7 text-foreground">{forced ? "Set a new password" : "Change password"}</h1>
            <p className="text-sm text-muted-foreground">
              {forced
                ? "Your password was set by an administrator. Choose your own to continue."
                : "Enter your current password and choose a new one."}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-5 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="currentPassword">{forced ? "Current (temporary) password" : "Current password"}</Label>
            <PasswordInput
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={status === "loading"}
              required
              autoFocus
              autoComplete="current-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="newPassword">New password</Label>
            <PasswordInput
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={status === "loading"}
              minLength={6}
              required
              autoComplete="new-password"
              aria-describedby="newPassword-hint"
            />
            <p id="newPassword-hint" className="text-xs text-muted-foreground">At least 6 characters.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword">Confirm new password</Label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={status === "loading"}
              minLength={6}
              required
              autoComplete="new-password"
              aria-invalid={mismatch}
              aria-describedby={mismatch ? "confirm-hint" : undefined}
            />
            {mismatch && <p id="confirm-hint" className="text-xs text-destructive">Passwords don't match yet.</p>}
          </div>

          {status === "error" && message && (
            <div role="alert" className="flex items-start gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden />
              {message}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            {!forced && (
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={status === "loading"} className={forced ? "w-full" : undefined}>
              {status === "loading" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating…</> : "Update password"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
